// CollapsableContainer.tsx

import { useState, useRef, useCallback, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCaretDown } from "@awesome.me/kit-f3e20eab48/icons/classic/solid";
import "./CollapsableContainer.scss";

interface CollapsableContainerProps {
    children: React.ReactNode;
    label?: string;
    maxHeight: string;
    minHeight: string;
}

export default function CollapsableContainer({
    children,
    label,
    maxHeight,
    minHeight,
}: CollapsableContainerProps) {
    const [expanded, setExpanded] = useState(false);

    /* ───────── scroll-shadow state ─────────── */
    const contentRef = useRef<HTMLDivElement>(null);
    const [topOpacity, setTopOpacity] = useState(0);
    const [bottomOpacity, setBottomOpacity] = useState(0);

    const handleToggle = () => {
        setExpanded((p) => !p);
        // Trigger shadow recalculation after state change
        requestAnimationFrame(() => {
            handleScroll();
        });
    };

    const handleScroll = useCallback(() => {
        const el = contentRef.current;
        if (!el) return;
        const { scrollTop, scrollHeight, clientHeight } = el;
        const maxScroll = Math.max(scrollHeight - clientHeight, 1);

        const MAX_OPACITY = 1;
        const SHADOW_FADE = 10; // px of fade-in/out

        setTopOpacity(Math.min(scrollTop / SHADOW_FADE, 1) * MAX_OPACITY);
        setBottomOpacity(
            Math.min((maxScroll - scrollTop) / SHADOW_FADE, 1) * MAX_OPACITY,
        );
    }, []);

    /* attach / detach scroll handler */
    useEffect(() => {
        handleScroll(); // run after first paint
        const el = contentRef.current;
        if (!el) return;

        el.addEventListener("scroll", handleScroll, { passive: true });
        return () => el.removeEventListener("scroll", handleScroll);
    }, [handleScroll]);

    // Update shadows when expanded state changes
    useEffect(() => {
        // Use requestAnimationFrame to ensure DOM has updated
        requestAnimationFrame(() => {
            handleScroll();
        });
    }, [expanded, handleScroll]);

    return (
        <div className="wrapper-collapsable-container">
            <div
                className={`collapsable-container${
                    expanded ? " is-expanded" : ""
                }`}
                style={{
                    maxHeight: expanded ? maxHeight : minHeight,
                    minHeight,
                }}
            >
                {/* ---- expand / collapse toggle ---- */}
                <button
                    type="button"
                    aria-label={expanded ? "Collapse" : "Expand"}
                    aria-expanded={expanded}
                    className="collapsable-container__expand-toggle"
                    onClick={handleToggle}
                >
                    <div className="collapsable-container__expand-toggle__content">
                        <FontAwesomeIcon icon={faCaretDown} />
                    </div>
                </button>

                {/* scrollable area */}
                <div
                    ref={contentRef}
                    className="collapsable-container__content"
                    onClick={expanded ? undefined : handleToggle}
                >
                    {label && (
                        <div className="collapsable-container__label">
                            {label}
                        </div>
                    )}
                    {children}
                </div>

                {topOpacity > 0 && (
                    <div
                        className="collapsable-container__shadow coll-top"
                        style={{ opacity: topOpacity }}
                    />
                )}
                {bottomOpacity > 0 && (
                    <div
                        className="collapsable-container__shadow coll-bottom"
                        style={{ opacity: bottomOpacity }}
                    />
                )}
            </div>
        </div>
    );
}
